import { ReactNode } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Base dialog configuration
export interface BaseDialogConfig {
  id: string;
  title: string;
  open: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  disableEscapeKeyDown?: boolean;
  disableBackdropClick?: boolean;
  PaperProps?: {
    sx?: Record<string, unknown>;
  };
}

// Confirm dialog configuration
export interface ConfirmDialogConfig extends BaseDialogConfig {
  type: 'confirm';
  message?: string;
  subtitle?: string;
  content?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?:
    'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  destructive?: boolean;
  requireConfirmText?: string;
  customActions?: ReactNode | ((confirmText: string) => ReactNode);
  extraActions?: ReactNode;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

// Form dialog configuration
export interface FormDialogConfig extends BaseDialogConfig {
  type: 'form';
  content: ReactNode;
  submitText?: string;
  cancelText?: string;
  submitDisabled?: boolean;
  destructive?: boolean;
  disableEnforceFocus?: boolean;
  rawContent?: boolean; // When true, content already includes DialogTitle, DialogContent, DialogActions
  extraActions?: ReactNode; // Additional actions to show on the left side of the dialog actions
  onSubmit: () => void | Promise<void>;
  onCancel?: () => void;
}

// Info dialog configuration
export interface InfoDialogConfig extends BaseDialogConfig {
  type: 'info';
  content: ReactNode;
  actions?: {
    text: string;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    variant?: 'text' | 'outlined' | 'contained';
    onClick: () => void;
  }[];
}

// Success dialog configuration
export interface SuccessDialogConfig extends BaseDialogConfig {
  type: 'success';
  message?: string;
  content?: ReactNode;
  actions?: {
    text: string;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    variant?: 'text' | 'outlined' | 'contained';
    onClick: () => void;
  }[];
}

// Loading dialog configuration
export interface LoadingDialogConfig extends BaseDialogConfig {
  type: 'loading';
  message: string;
}

export type DialogConfig =
  | ConfirmDialogConfig
  | FormDialogConfig
  | InfoDialogConfig
  | SuccessDialogConfig
  | LoadingDialogConfig;

interface DialogState {
  dialogs: Map<string, DialogConfig>;
  activeDialogIds: string[];
}

interface DialogActions {
  openDialog: (config: DialogConfig) => string;
  closeDialog: (id: string) => void;
  closeAllDialogs: () => void;
  updateDialog: (id: string, updates: Partial<DialogConfig>) => void;

  confirm: (options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    destructive?: boolean;
    requireConfirmText?: string;
    onConfirm: () => void | Promise<void>;
    onCancel?: () => void;
  }) => string;

  info: (options: {
    title: string;
    content: ReactNode;
    actions?: InfoDialogConfig['actions'];
  }) => string;

  success: (options: {
    title: string;
    message?: string;
    content?: ReactNode;
    actions?: SuccessDialogConfig['actions'];
  }) => string;

  form: (options: {
    title: string;
    content: ReactNode;
    submitText?: string;
    cancelText?: string;
    submitDisabled?: boolean;
    onSubmit: () => void | Promise<void>;
    onCancel?: () => void;
    maxWidth?: BaseDialogConfig['maxWidth'];
  }) => string;

  loading: (options: { title: string; message: string }) => string;
}

type DialogStore = DialogState & DialogActions;

const generateDialogId = (): string =>
  `dialog_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useDialogStore = create<DialogStore>()(
  devtools(
    (set, get) => ({
      dialogs: new Map(),
      activeDialogIds: [],

      openDialog: (config: DialogConfig) => {
        const id = config.id || generateDialogId();
        const dialogConfig = { ...config, id, open: true };

        set((state) => {
          const newDialogs = new Map(state.dialogs);
          newDialogs.set(id, dialogConfig);

          return {
            dialogs: newDialogs,
            activeDialogIds: [...state.activeDialogIds, id],
          };
        });

        return id;
      },

      closeDialog: (id: string) => {
        set((state) => {
          const newDialogs = new Map(state.dialogs);
          const dialog = newDialogs.get(id);

          if (dialog) {
            newDialogs.set(id, { ...dialog, open: false });
            const newActiveIds = state.activeDialogIds.filter(
              (dialogId) => dialogId !== id,
            );

            setTimeout(() => {
              set((currentState) => {
                const cleanedDialogs = new Map(currentState.dialogs);
                cleanedDialogs.delete(id);
                return { dialogs: cleanedDialogs };
              });
            }, 300);

            return {
              dialogs: newDialogs,
              activeDialogIds: newActiveIds,
            };
          }

          return state;
        });
      },

      closeAllDialogs: () => {
        set((state) => {
          const newDialogs = new Map();
          state.dialogs.forEach((dialog, id) => {
            newDialogs.set(id, { ...dialog, open: false });
          });

          setTimeout(() => {
            set({ dialogs: new Map(), activeDialogIds: [] });
          }, 300);

          return {
            dialogs: newDialogs,
            activeDialogIds: [],
          };
        });
      },

      updateDialog: (id: string, updates: Partial<DialogConfig>) => {
        set((state) => {
          const newDialogs = new Map(state.dialogs);
          const dialog = newDialogs.get(id);

          if (dialog) {
            newDialogs.set(id, { ...dialog, ...updates } as DialogConfig);
            return { dialogs: newDialogs };
          }

          return state;
        });
      },

      confirm: (options) => {
        const id = generateDialogId();
        const config: ConfirmDialogConfig = {
          id,
          type: 'confirm',
          title: options.title,
          message: options.message,
          confirmText: options.confirmText || 'Confirm',
          cancelText: options.cancelText || 'Cancel',
          destructive: options.destructive || false,
          confirmColor: options.destructive ? 'error' : 'primary',
          requireConfirmText: options.requireConfirmText,
          open: true,
          maxWidth: 'sm',
          onConfirm: options.onConfirm,
          onCancel: options.onCancel,
        };

        return get().openDialog(config);
      },

      info: (options) => {
        const id = generateDialogId();
        const config: InfoDialogConfig = {
          id,
          type: 'info',
          title: options.title,
          content: options.content,
          actions: options.actions,
          open: true,
          maxWidth: 'sm',
        };

        return get().openDialog(config);
      },

      success: (options) => {
        const id = generateDialogId();
        const config: SuccessDialogConfig = {
          id,
          type: 'success',
          title: options.title,
          message: options.message,
          content: options.content,
          actions: options.actions || [
            {
              text: 'OK',
              color: 'primary',
              variant: 'contained',
              onClick: () => get().closeDialog(id),
            },
          ],
          open: true,
          maxWidth: 'sm',
        };

        return get().openDialog(config);
      },

      form: (options) => {
        const id = generateDialogId();
        const config: FormDialogConfig = {
          id,
          type: 'form',
          title: options.title,
          content: options.content,
          submitText: options.submitText || 'Submit',
          cancelText: options.cancelText || 'Cancel',
          submitDisabled: options.submitDisabled || false,
          open: true,
          maxWidth: options.maxWidth || 'md',
          onSubmit: options.onSubmit,
          onCancel: options.onCancel,
        };

        return get().openDialog(config);
      },

      loading: (options) => {
        const id = generateDialogId();
        const config: LoadingDialogConfig = {
          id,
          type: 'loading',
          title: options.title,
          message: options.message,
          open: true,
          maxWidth: 'sm',
          disableEscapeKeyDown: true,
          disableBackdropClick: true,
        };

        return get().openDialog(config);
      },
    }),
    {
      name: 'dialog-store',
    },
  ),
);
